import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { LogOut, Plus, Edit, Trash2, Shield, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ admin, onLogout }) => {
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    pincode: '',
    google_sheet_url: '',
    order_link: '',
    upload_manuscript_link: '',
    view_audiobooks_link: '',
    email_production_link: ''
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchPublishers();
  }, []);

  const fetchPublishers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/publishers?pincode=${admin.pincode}`);
      setPublishers(response.data.publishers || []);
      toast.success("Publishers loaded successfully");
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to load publishers";
      toast.error(message);
      console.error("Error fetching publishers:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePublisher = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await axios.post(`${API}/admin/publishers?pincode=${admin.pincode}`, formData);
      toast.success("Publisher created successfully");
      setShowCreateModal(false);
      setFormData({ name: '', pincode: '', google_sheet_url: '', order_link: '', upload_manuscript_link: '', view_audiobooks_link: '', email_production_link: '' });
      fetchPublishers();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to create publisher";
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditPublisher = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await axios.put(
        `${API}/admin/publishers/${selectedPublisher.id}?pincode=${admin.pincode}`, 
        formData
      );
      toast.success("Publisher updated successfully");
      setShowEditModal(false);
      setSelectedPublisher(null);
      setFormData({ name: '', pincode: '', google_sheet_url: '', order_link: '', upload_manuscript_link: '', view_audiobooks_link: '', email_production_link: '' });
      fetchPublishers();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to update publisher";
      toast.error(message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeletePublisher = async (publisherId, publisherName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${publisherName}?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/publishers/${publisherId}?pincode=${admin.pincode}`);
      toast.success("Publisher deactivated successfully");
      fetchPublishers();
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to deactivate publisher";
      toast.error(message);
    }
  };

  const openCreateModal = () => {
    setFormData({ name: '', pincode: '', google_sheet_url: '', order_link: '', upload_manuscript_link: '', view_audiobooks_link: '', email_production_link: '' });
    setShowCreateModal(true);
  };

  const openEditModal = (publisher) => {
    setSelectedPublisher(publisher);
    setFormData({
      name: publisher.name,
      pincode: publisher.pincode,
      google_sheet_url: publisher.google_sheet_url,
      order_link: publisher.order_link || '',
      upload_manuscript_link: publisher.upload_manuscript_link || '',
      view_audiobooks_link: publisher.view_audiobooks_link || '',
      email_production_link: publisher.email_production_link || ''
    });
    setShowEditModal(true);
  };

  const generateRandomPincode = () => {
    const pincode = Math.random().toString().slice(2, 8).padEnd(6, '0');
    setFormData(prev => ({ ...prev, pincode }));
  };

  const activePublishers = publishers.filter(p => p.active);
  const inactivePublishers = publishers.filter(p => !p.active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-title">
                  Admin Dashboard
                </h1>
                <p className="text-sm text-gray-600">Manage publishers and access</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              onClick={onLogout}
              className="flex items-center space-x-2 hover:bg-red-50 hover:text-red-600 transition-colors"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats and Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardDescription className="text-sm font-medium text-gray-600">Total Publishers</CardDescription>
              <CardTitle className="text-3xl font-bold text-gray-900" data-testid="total-publishers">
                {publishers.length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardDescription className="text-sm font-medium text-gray-600">Active Publishers</CardDescription>
              <CardTitle className="text-3xl font-bold text-green-600" data-testid="active-publishers">
                {activePublishers.length}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-card">
            <CardHeader className="pb-2">
              <CardDescription className="text-sm font-medium text-gray-600">Actions</CardDescription>
              <CardTitle>
                <Button 
                  onClick={openCreateModal}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white"
                  data-testid="create-publisher-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Publisher
                </Button>
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Publishers List */}
        <div className="space-y-6">
          {/* Active Publishers */}
          {activePublishers.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Active Publishers ({activePublishers.length})
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {activePublishers.map((publisher) => (
                  <Card key={publisher.id} className="project-card">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                            {publisher.name}
                          </CardTitle>
                          <CardDescription className="text-sm text-gray-600 mt-1">
                            Pincode: {publisher.pincode}
                          </CardDescription>
                        </div>
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Active
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Google Sheet</label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-600 truncate flex-1">
                            {publisher.google_sheet_url}
                          </p>
                          <a 
                            href={publisher.google_sheet_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 pt-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openEditModal(publisher)}
                          className="flex-1"
                          data-testid={`edit-publisher-${publisher.id}`}
                        >
                          <Edit className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleDeletePublisher(publisher.id, publisher.name)}
                          className="flex-1 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                          data-testid={`delete-publisher-${publisher.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Deactivate
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Inactive Publishers */}
          {inactivePublishers.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-600 mb-4 flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Inactive Publishers ({inactivePublishers.length})
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {inactivePublishers.map((publisher) => (
                  <Card key={publisher.id} className="project-card opacity-60">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg font-semibold text-gray-900 truncate">
                            {publisher.name}
                          </CardTitle>
                          <CardDescription className="text-sm text-gray-600 mt-1">
                            Pincode: {publisher.pincode}
                          </CardDescription>
                        </div>
                        <Badge variant="outline" className="bg-gray-100 text-gray-600">
                          Inactive
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Google Sheet</label>
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {publisher.google_sheet_url}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {publishers.length === 0 && !loading && (
            <div className="text-center py-20">
              <div className="space-y-4">
                <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto flex items-center justify-center">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">No publishers found</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  Get started by creating your first publisher account.
                </p>
                <Button onClick={openCreateModal} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Publisher
                </Button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center space-y-4">
                <div className="loading-spinner mx-auto w-8 h-8 border-4 border-red-200 border-t-red-600"></div>
                <p className="text-gray-600">Loading publishers...</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Create Publisher Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Publisher</DialogTitle>
            <DialogDescription>
              Add a new publisher with access to their project data.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreatePublisher} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Publisher Name</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter publisher name"
                required
                data-testid="publisher-name-input"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Pincode</label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={generateRandomPincode}
                  data-testid="generate-pincode-button"
                >
                  Generate
                </Button>
              </div>
              <Input 
                value={formData.pincode}
                onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                placeholder="Enter 6-digit pincode"
                maxLength={10}
                required
                data-testid="publisher-pincode-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Google Sheet URL</label>
              <Textarea 
                value={formData.google_sheet_url}
                onChange={(e) => setFormData(prev => ({ ...prev, google_sheet_url: e.target.value }))}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                required
                rows={2}
                data-testid="google-sheet-url-input"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 border-b pb-2">Publisher Action Links</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order Link (Lägg beställning)</label>
                <Input 
                  value={formData.order_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, order_link: e.target.value }))}
                  placeholder="https://example.com/order"
                  data-testid="order-link-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Manuscript Link (Ladda upp manus)</label>
                <Input 
                  value={formData.upload_manuscript_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, upload_manuscript_link: e.target.value }))}
                  placeholder="https://example.com/upload"
                  data-testid="upload-link-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">View Audiobooks Link (Se beställda ljudböcker)</label>
                <Input 
                  value={formData.view_audiobooks_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, view_audiobooks_link: e.target.value }))}
                  placeholder="https://example.com/audiobooks"
                  data-testid="audiobooks-link-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Production Link (Skicka ett mail till produktionsavdelningen)</label>
                <Input 
                  value={formData.email_production_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, email_production_link: e.target.value }))}
                  placeholder="mailto:produktioner@visueltljud.se"
                  data-testid="email-production-link-input"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateModal(false)}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={formLoading}
                data-testid="submit-create-publisher"
              >
                {formLoading ? "Creating..." : "Create Publisher"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Publisher Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Publisher</DialogTitle>
            <DialogDescription>
              Update publisher information and access credentials.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditPublisher} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Publisher Name</label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter publisher name"
                required
                data-testid="edit-publisher-name-input"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Pincode</label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={generateRandomPincode}
                >
                  Generate New
                </Button>
              </div>
              <Input 
                value={formData.pincode}
                onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                placeholder="Enter 6-digit pincode"
                maxLength={10}
                required
                data-testid="edit-publisher-pincode-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Google Sheet URL</label>
              <Textarea 
                value={formData.google_sheet_url}
                onChange={(e) => setFormData(prev => ({ ...prev, google_sheet_url: e.target.value }))}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                required
                rows={2}
                data-testid="edit-google-sheet-url-input"
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 border-b pb-2">Publisher Action Links</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order Link (Lägg beställning)</label>
                <Input 
                  value={formData.order_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, order_link: e.target.value }))}
                  placeholder="https://example.com/order"
                  data-testid="edit-order-link-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Upload Manuscript Link (Ladda upp manus)</label>
                <Input 
                  value={formData.upload_manuscript_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, upload_manuscript_link: e.target.value }))}
                  placeholder="https://example.com/upload"
                  data-testid="edit-upload-link-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">View Audiobooks Link (Se beställda ljudböcker)</label>
                <Input 
                  value={formData.view_audiobooks_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, view_audiobooks_link: e.target.value }))}
                  placeholder="https://example.com/audiobooks"
                  data-testid="edit-audiobooks-link-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Production Link (Skicka ett mail till produktionsavdelningen)</label>
                <Input 
                  value={formData.email_production_link}
                  onChange={(e) => setFormData(prev => ({ ...prev, email_production_link: e.target.value }))}
                  placeholder="mailto:produktioner@visueltljud.se"
                  data-testid="edit-email-production-link-input"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowEditModal(false)}
                disabled={formLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={formLoading}
                data-testid="submit-edit-publisher"
              >
                {formLoading ? "Updating..." : "Update Publisher"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
